import { ApiError } from '@/internationalization/api-client'
import { SUPPORT_LIMITS, type FileProblem } from './validation'

export type T = (key: string, vars?: Record<string, string | number>) => string

const MB = 1024 * 1024
export const FILE_VARS = {
  max: SUPPORT_LIMITS.maxFileBytes / MB,
  total: SUPPORT_LIMITS.maxTotalBytes / MB,
}

// One literal `t()` call per key, not a composed one: `keys.test.ts` can only
// verify a key it can read, and a key nobody defined renders as itself.
export function fileMessage(t: T, problem: FileProblem): string {
  switch (problem) {
    case 'unsupported':
      return t('support.error.file.unsupported')
    case 'empty':
      return t('support.error.file.empty')
    case 'too_large':
      return t('support.error.file.tooLarge', FILE_VARS)
    case 'too_many':
      return t('support.error.file.tooMany', { max: SUPPORT_LIMITS.maxFiles })
    case 'total_too_large':
      return t('support.error.file.totalTooLarge', FILE_VARS)
  }
}

/**
 * Codes the server answers ONLY on a path that provably sent nothing: a refusal
 * before the mailer was called (validation, rate limit, not configured, limiter
 * down), a transport failure raised before Graph accepted anything
 * (`support_unavailable`, a token or connect failure) and a definite Graph
 * refusal (`support_send_failed`). `support_not_delivered` is the 202 the client
 * itself raises when nothing was delivered (a render-only transport).
 */
const NOT_SENT_CODES: ReadonlySet<string> = new Set([
  'support_not_configured',
  'support_not_delivered',
  'support_rate_limited',
  'support_rate_limiter_unavailable',
  'support_unavailable',
  'support_send_failed',
  'invalid_support_request',
  'request_too_large',
])

/**
 * What the browser can honestly claim about the email.
 *
 *  - `not_sent`: the server told us, by a code it only uses on a path that sent
 *    nothing, or the request was refused with a 4xx (nothing was processed).
 *  - `unknown`: everything else -- `support_send_unconfirmed`, a connection that
 *    dropped, and ANY 5xx without one of those codes. A gateway answers 503 for a
 *    throttled function (nothing sent) and for a 30 s integration timeout
 *    (possibly sent) with no way to tell them apart, and an uncaught 500 can
 *    follow a send too. The UI must not say "not sent" for these.
 *
 * Success is not classified here: it is a 2xx with `delivered: true`.
 */
export type SendOutcome = 'not_sent' | 'unknown'

export function sendOutcome(error: unknown): SendOutcome {
  if (!(error instanceof ApiError)) return 'unknown'
  if (error.code === 'support_send_unconfirmed') return 'unknown'
  if (NOT_SENT_CODES.has(error.code)) return 'not_sent'
  if (error.status >= 400 && error.status < 500) return 'not_sent'
  if (error.status === 202) return 'not_sent'
  return 'unknown'
}

/** True when nobody can say whether the request was delivered. */
export function isUnconfirmed(error: unknown): boolean {
  return sendOutcome(error) === 'unknown'
}

/**
 * True when the form cannot work and the person should be offered an email
 * address instead. Deliberately narrow: an UNCONFIRMED send (any `unknown`
 * outcome) may already have reached us, so emailing too could make two
 * requests; a rate limit or a validation error is not a reason to go
 * elsewhere, and a transient 502 is worth a retry.
 */
export function offersEmailFallback(error: unknown): boolean {
  return (
    sendOutcome(error) === 'not_sent' &&
    error instanceof ApiError &&
    (error.code === 'support_not_configured' ||
      error.code === 'support_not_delivered' ||
      error.code === 'support_send_failed' ||
      error.code === 'support_rate_limiter_unavailable')
  )
}

/**
 * Whether the error state shows an "or email us" line: the failure must be one
 * a retry cannot fix AND the deployment must have named an address. No address,
 * no line -- an unverified one is worse than none.
 */
export function showsEmailFallback(error: unknown, address: string | null): boolean {
  return address !== null && address.trim() !== '' && offersEmailFallback(error)
}

/**
 * What to tell the person when the server refused or the request never
 * arrived. Branches on the server's `code`, never on a substring of its
 * message, and a non-ApiError (fetch itself threw) is a network failure.
 */
export function serverMessage(t: T, error: unknown): string {
  // Before any code is read: an outcome nobody can confirm gets the one message
  // that does not claim either way, whatever the transport called it.
  if (isUnconfirmed(error)) return t('support.error.sendUnconfirmed')
  if (!(error instanceof ApiError)) return t('support.error.generic')
  switch (error.code) {
    case 'subject_required':
      return t('support.error.subject.required')
    case 'subject_too_long':
      return t('support.error.subject.tooLong', { max: SUPPORT_LIMITS.subjectMax })
    case 'description_required':
      return t('support.error.description.required')
    case 'description_too_long':
      return t('support.error.description.tooLong', { max: SUPPORT_LIMITS.descriptionMax })
    case 'invalid_characters':
      return t('support.error.invalidCharacters')
    case 'attachment_unsupported':
      return fileMessage(t, 'unsupported')
    case 'attachment_empty':
      return fileMessage(t, 'empty')
    case 'attachment_too_large':
      return fileMessage(t, 'too_large')
    case 'attachments_too_large':
      return fileMessage(t, 'total_too_large')
    case 'too_many_attachments':
      return fileMessage(t, 'too_many')
    case 'request_too_large':
      return t('support.error.tooLarge')
    case 'support_not_configured':
    case 'support_not_delivered':
      return t('support.error.notConfigured')
    case 'support_unavailable':
    case 'support_rate_limiter_unavailable':
      return t('support.error.unavailable')
    case 'support_rate_limited': {
      // `details` is the server's word and typed `unknown`: only a finite,
      // positive number is believed. Retrying into a limit cannot help, so the
      // message says to wait rather than to try again.
      const seconds = (error.details as { retryAfterSeconds?: unknown } | null | undefined)
        ?.retryAfterSeconds
      if (typeof seconds === 'number' && Number.isFinite(seconds) && seconds > 0) {
        return t('support.error.rateLimited', { minutes: Math.max(1, Math.ceil(seconds / 60)) })
      }
      return t('support.error.rateLimitedNoWait')
    }
    case 'invalid_support_request':
      return t('support.error.unreadable')
    case 'support_send_failed':
      return t('support.error.sendFailed')
    default:
      return t('support.error.generic')
  }
}
